// 2026-08-03 - FormuLearning - Step 4 성분표 (이슈 #52 전치 · #53 분리)
// 2026-08-31 수정 - 이슈 #58 S5(#68): `M84-09`·`10`·`62` 반영.
//                   ① 처방 열마다 **`mg/T`·`%` 2개 서브컬럼**(중량 왼쪽·비율 오른쪽)
//                   ② 성분명 오른쪽 **`설정 범위` 열** — mode별 `5.0~30.0%`/`Balanced`/`30~50mg`
//                   ③ 셀 편집·상호 환산 — 어느 쪽을 고쳐도 반대쪽이 따라오고 `%`로 저장한다
//                   ④ 입력 제약 — non-weight 는 `%` 불가(mg만), balance 는 양쪽 불가
// 2026-11-02 수정 - 이슈 #101(M1002-02·09): ① `%` 칸 표시를 **소수점 둘째 자리 + 합계 100.00 보정**으로
//                   (`lib/composition.ts` `pctDisplay`, 저장은 셋째 자리 그대로) ② **`Total (%)` 행** 신설 —
//                   중량 성분(balance 포함, non-weight 제외) 합, 100 ± 0.1%p 밖이면 경고색 ③ 표시가 저장값보다
//                   거칠어졌으므로 **편집하지 않은 칸의 blur 는 저장하지 않는다**(표시값 덮어쓰기 방지).
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 조성 셀·계산 제외/복원 비활성(배치 선택·액션 이력은 열어 둔다).
// 2026-11-03 수정 - 이슈 #102: ① 행 순서 = Step 3 X 순서(`orderByXVariables`, M1002-07). Step 3 X 행은 값이 없어도
//                   보인다(User Define 빈칸) ② balance 칸에 «Balanced» 대신 **저장된 계산값**(읽기 전용, M1002-08)
//                   ③ User Define 초기 설계 배치는 칸을 비우면 `null` 로 저장(키 제거)하고 빈칸을 강조한다(M1002-05 D4).
// 파일 위치: frontend/src/features/step4/ComponentTable.tsx
// 목적: UI/UX v2.2 §5.8 `COMP-COMPONENT-TABLE` 구현체 — **행 = excipient · 열 = batch**,
//       하단에 y행(f2·Y변수). 성분 수는 고정(10~15)이고 배치는 계속 늘어나므로 성분을
//       세로에 고정해야 같은 성분을 배치 간 좌우로 비교할 수 있다.
//
//       배치 조작은 열 헤더가 진다 — **선택은 헤더·셀 클릭**, 업로드·제외/복원·이력은
//       **`⋯` 팝오버**다(열 폭이 좁아 아이콘 3종 상시 노출 대신 접었다).
//
//       상태는 갖지 않는다. 선택 상태·조작 핸들러는 `IntegrationTable`이 보유하고
//       props로 내려준다 — f2 차트·Per-Batch 패널과 단일 선택을 공유하기 때문이다(#50 S-1·S-2).
//       (편집 중인 셀 1칸의 원문만 예외로 쥔다 — 공유되지 않는 표시 상태다.)

import { useMemo, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { EXCLUDE_TYPE_LABELS, type LotBatch } from '../../api/lot'
import type { XVariable } from '../../api/step3'
import Popover from '../../components/common/Popover'
import { NON_WEIGHT_MODE, canClearComposition, orderByXVariables, pctDisplay } from '../../lib/composition'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  /** 필터를 통과한 배치 = 표의 열. 필터칩은 상위(IntegrationTable)가 쥔다. */
  visible: LotBatch[]
  selected: LotBatch | null
  onSelect: (batchId: string) => void
  /** 저장 진행 중이면 조작을 잠근다. */
  busy: boolean
  onExclude: (batch: LotBatch) => void
  onRestore: (batch: LotBatch) => void
  onHistory: (batch: LotBatch) => void
  /** 설정 범위 열·입력 제약의 근거(Step 3 §3.2). 성분명으로 맞춘다. */
  xVariables: XVariable[]
  /** `mg/T` 환산 기준(Step 3 §3.1). 없으면 mg 칸은 `—`이고 입력도 막힌다. */
  totalWeightMg: number | null
  /** 셀 편집 저장 — 값은 **% 단위**다(인터페이스 v1.8 §2.10.4). `null` = 칸 비우기(User Define, #102). */
  onSaveComposition: (batch: LotBatch, actualX: Record<string, number | null>) => Promise<void>
  /** Step 3 초기 설계 알고리즘 — User Define 이면 init 배치 칸을 비울 수 있다(#102 D4). */
  runsAlgorithm?: string | null
}

/** f2는 전용 행이 있으므로 Y 행에서 뺀다 — 같은 값을 두 번 보여주지 않는다(#42 잔여 정리). */
const isF2 = (name: string) => name.trim().toLowerCase().startsWith('f2')

/** 열 헤더는 좁다 — Lot 접두(`L1-`)를 떼고 `init-1`만 남긴다(프로토타입 renderComp, #52). */
const shortCode = (code: string) => code.replace(/^L\d+-/, '')

/** 저장 정밀도 — 회의록 260804 L39 "소수점 셋째자리 반올림해 저장한다"(`M84-09`). */
const round3 = (n: number) => Math.round(n * 1000) / 1000

/** 표 셀 숫자 표기 — 값이 없으면 `—`. 뒤따르는 0은 떼서 열 폭을 아낀다. */
const fmt3 = (v: unknown): string =>
  typeof v === 'number' && Number.isFinite(v) ? String(round3(v)) : '—'

/** y행 표기 — 종전 그대로 소수 1자리. */
const fmtNum = (v: unknown) => (typeof v === 'number' ? v.toFixed(1) : '—')

/** 설정 범위의 % 표기 — 정본 예시(`5.0~30.0`)를 따르되 소수 2자리는 살린다. */
const fmtPct = (n: number): string => {
  const r = Math.round(n * 100) / 100
  return Number.isInteger(r) ? r.toFixed(1) : String(r)
}

/** 유형 칩 — init은 teal, opti는 purple(프로토타입 chip.init/chip.opti). */
const chipStyle = (t: LotBatch['batch_type']): React.CSSProperties =>
  t === 'init'
    ? { background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }
    : { background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }

/**
 * 성분명 열 고정 — 배치가 늘면 열이 늘어 가로 스크롤이 생긴다(#52). 표는
 * `border-collapse: collapse`라 테두리가 셀과 함께 밀리므로 오른쪽 경계는
 * box-shadow로 긋는다.
 */
const STICKY_HEAD: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  background: 'var(--color-bg-panel)',
  boxShadow: '1px 0 0 var(--border-subtle)',
}

/** 배치들의 키 합집합. init·opti가 서로 다른 변수 집합을 가질 수 있다. */
function unionKeys(batches: LotBatch[], pick: (b: LotBatch) => Record<string, number>): string[] {
  const keys: string[] = []
  for (const b of batches) {
    for (const k of Object.keys(pick(b) || {})) if (!keys.includes(k)) keys.push(k)
  }
  return keys
}

/**
 * 설정 범위 표기(`M84-62`) — mode마다 형태가 다르다.
 * non-weight 는 **`mg`** 로 적는다: 회의록 260804 L41~42 에 `mg/T` 라고 했다가
 * *"mg/T아니고 mg으로 보여주자"* 로 정정된 대목이다(등록부 v1.17 정정분).
 */
function rangeLabel(x: XVariable | undefined, total: number | null): string {
  if (!x) return '—'
  if (x.mode === 'balance') return 'Balanced'
  const lo = Number(x.min_pct) || 0
  const hi = Number(x.max_pct) || 0
  if (x.mode === 'selected(non-weight)') {
    if (!total || total <= 0) return '—'
    const a = round3((lo * total) / 100)
    const b = round3((hi * total) / 100)
    return a === b ? `${a}mg` : `${a}~${b}mg`
  }
  return lo === hi ? `${fmtPct(lo)}%` : `${fmtPct(lo)}~${fmtPct(hi)}%`
}

type Unit = 'mg' | 'pct'
type EditState = { batchId: string; key: string; unit: Unit; raw: string }

/** 편집 가능 여부(`M84-10`) — balance 는 양쪽 불가, non-weight 는 `%` 불가. */
function editable(x: XVariable | undefined, unit: Unit, hasTotal: boolean): boolean {
  if (x?.mode === 'balance') return false
  if (unit === 'pct') return x?.mode !== 'selected(non-weight)'
  return hasTotal // mg 는 총중량이 있어야 %로 되돌릴 수 있다
}

export default function ComponentTable({
  visible, selected, onSelect, busy, onExclude, onRestore, onHistory,
  xVariables, totalWeightMg, onSaveComposition, runsAlgorithm,
}: Props) {
  const canWrite = useCanWrite()
  // 행 = 배치 키 합집합 + Step 3 X 이름(값이 없어도 행이 있어야 빈칸을 채운다, #102) → Step 3 X 순서(M1002-07).
  const xKeys = useMemo(() => {
    const keys = unionKeys(visible, (b) => b.x_values)
    if (visible.length > 0) {
      const seen = new Set(keys.map((k) => k.trim().toLowerCase()))
      for (const x of xVariables ?? []) {
        const name = (x.excipient ?? '').trim()
        if (name && !seen.has(name.toLowerCase())) { keys.push(name); seen.add(name.toLowerCase()) }
      }
    }
    return orderByXVariables(keys, xVariables)
  }, [visible, xVariables])
  const yKeys = useMemo(
    () => unionKeys(visible, (b) => b.y_values).filter((k) => !isF2(k)),
    [visible],
  )
  // 설정 범위·입력 제약의 근거. 성분명은 표기 흔들림(대소문자·앞뒤 공백)이 있어 정규화해 맞춘다.
  const xByName = useMemo(() => {
    const m = new Map<string, XVariable>()
    for (const x of xVariables ?? []) {
      if (x.excipient) m.set(x.excipient.trim().toLowerCase(), x)
    }
    return m
  }, [xVariables])
  const varOf = (key: string) => xByName.get(key.trim().toLowerCase())
  // 배치별 % 표시값·합계(M1002-09) — non-weight 는 % 합에서 뺀다(M1002-02).
  const pctByBatch = useMemo(() => {
    const isNonWeight = (k: string) => xByName.get(k.trim().toLowerCase())?.mode === NON_WEIGHT_MODE
    return new Map(visible.map((b) => [b.id, pctDisplay(b.x_values, isNonWeight)]))
  }, [visible, xByName])

  const total = totalWeightMg && totalWeightMg > 0 ? totalWeightMg : null
  const hasTotal = total !== null

  // 편집 중인 **한 칸**의 원문만 쥔다. 확정된 값은 항상 props(b.x_values)가 정본이다.
  const [edit, setEdit] = useState<EditState | null>(null)
  const [saving, setSaving] = useState(false)

  const pctToMg = (pct: unknown): number | null =>
    typeof pct === 'number' && Number.isFinite(pct) && total ? round3((pct * total) / 100) : null

  /** blur/Enter 확정 — % 로 환산해 저장한다. 값이 그대로면 호출하지 않는다. */
  const commit = async (b: LotBatch, key: string, unit: Unit, raw: string) => {
    // 편집하지 않은 칸의 blur — 표시값(둘째 자리)이 저장값(셋째 자리)을 덮어쓰지 않게 한다(#101).
    const touched = edit?.batchId === b.id && edit.key === key && edit.unit === unit
    setEdit(null)
    if (!touched) return
    // 칸 비우기(#102 D4) — User Define 초기 설계 배치만. 서버가 키를 지우고 balance 도 비운다.
    if (raw.trim() === '' && canClearComposition(b, runsAlgorithm) && typeof b.x_values[key] === 'number') {
      setSaving(true)
      try {
        await onSaveComposition(b, { ...b.x_values, [key]: null })
      } finally {
        setSaving(false)
      }
      return
    }
    const n = Number(raw)
    if (raw.trim() === '' || !Number.isFinite(n) || n < 0) return
    const pct = unit === 'pct' ? n : total ? (n * 100) / total : null
    if (pct === null || pct > 100) return
    const next = round3(pct)
    const cur = b.x_values[key]
    if (typeof cur === 'number' && round3(cur) === next) return
    setSaving(true)
    try {
      await onSaveComposition(b, { ...b.x_values, [key]: next })
    } finally {
      setSaving(false)
    }
  }

  const onCellKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
      return
    }
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') {
      setEdit(null)
      e.currentTarget.blur()
    }
  }

  // 배치 열의 공통 표시(#52) — 선택 열은 강조, 제외 열은 흐림. 행 단위였던 두 표시를
  // 전치 후에는 열의 셀마다 걸어야 한다(열 전체를 잡는 선택자가 없다).
  const colStyle = (b: LotBatch): React.CSSProperties => ({
    opacity: b.is_excluded ? 0.45 : 1,
    // 선택 열 하이라이트 — f2 차트 포인트·Per-Batch 패널과 연동(S-1·S-2)
    background: selected?.id === b.id ? 'var(--bg-chip-teal)' : undefined,
  })

  /** 값 칸 1개 — 편집 가능하면 무테 입력, 아니면 텍스트. 클릭은 그대로 열 선택으로 흐른다. */
  const valueCell = (b: LotBatch, key: string, unit: Unit) => {
    const x = varOf(key)
    const pct = b.x_values[key]
    const shown = unit === 'pct' ? (pctByBatch.get(b.id)?.shown[key] ?? fmt3(pct)) : fmt3(pctToMg(pct))
    // balance — 저장된 계산값을 읽기 전용으로(M1002-08). 중량 성분이 비어 있으면 값이 없다(`—`).
    if (x?.mode === 'balance') {
      return (
        <span className="text-[11px] text-text-secondary" title="Balanced — 100% 에서 나머지 중량 성분을 뺀 자동 계산값">
          {shown}
        </span>
      )
    }
    if (!editable(x, unit, hasTotal)) {
      // non-weight 의 `%` 칸, 총중량 미입력 시의 mg 칸 — 값 대신 사유를 남긴다.
      const why = unit === 'pct'
        ? 'selected(non-weight) — % 입력 불가 (mg 로만 관리합니다)'
        : '총중량(Step 3 §3.1)을 먼저 입력하십시오'
      return <span className="text-[10px] text-text-muted" title={why}>—</span>
    }
    const isEditing = edit?.batchId === b.id && edit.key === key && edit.unit === unit
    // User Define 빈칸 강조(#102 D6) — 다 채워야 Next Optimization 이 열린다.
    const blank = canClearComposition(b, runsAlgorithm) && !(typeof pct === 'number' && Number.isFinite(pct))
    return (
      <input
        type="number"
        min={0}
        step={unit === 'pct' ? 0.01 : 0.001}
        value={isEditing ? edit.raw : shown === '—' ? '' : shown}
        placeholder="—"
        disabled={busy || saving || !canWrite}
        onChange={(e) => setEdit({ batchId: b.id, key, unit, raw: e.target.value })}
        onBlur={(e) => void commit(b, key, unit, e.target.value)}
        onKeyDown={onCellKeyDown}
        title={canWrite
          ? `${b.batch_code} · ${key} (${unit === 'mg' ? 'mg/T' : '%'})${blank ? ' — User Define 값 입력 필요' : ''}`
          : READ_ONLY_TITLE}
        style={blank ? { boxShadow: 'inset 0 0 0 1px var(--color-warn)' } : undefined}
        className="w-14 rounded bg-transparent px-1 py-0.5 text-center text-[11px] text-text-body outline-none transition-colors hover:bg-bg-input focus:bg-bg-input disabled:opacity-40"
      />
    )
  }

  return (
    <>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
        <table className="min-w-full text-[11px]">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <th rowSpan={2} className="px-2 py-1.5 text-left font-medium text-text-secondary" style={STICKY_HEAD}>
                Excipient
              </th>
              {/* 설정 범위 열(M84-62) — Step 3 X Variables 설정을 그대로 비춘다. */}
              <th
                rowSpan={2}
                className="whitespace-nowrap px-2 py-1.5 text-center font-medium text-text-secondary"
                title="Step 3 §3.2 X Variables 의 mode·범위를 그대로 비춥니다"
              >
                설정 범위
              </th>
              {visible.map((b) => (
                <th
                  key={b.id}
                  colSpan={2}
                  onClick={() => onSelect(b.id)}
                  title={`${b.batch_code} — 클릭하면 아래 f2 추이·용출 패널이 이 배치로 바뀝니다`}
                  className="cursor-pointer whitespace-nowrap px-2 py-1.5 align-top font-medium"
                  style={colStyle(b)}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="flex items-center gap-0.5">
                      <span className="text-text-body">{shortCode(b.batch_code)}</span>
                      {/* 조작 2종 — 열 폭이 좁아 팝오버로 접었다(#52 D-1).
                          업로드 항목은 S5 5차에서 폐지됐다(`M84-75`, BR-S4-L14·L16 개정). */}
                      <Popover
                        width={170}
                        label={`${b.batch_code} 조작`}
                        trigger={
                          <span className="px-0.5 text-[11px] text-text-secondary hover:text-accent-teal">⋯</span>
                        }
                      >
                        {(close) => (
                          <div className="flex flex-col gap-0.5">
                            <p
                              className="mb-1 border-b pb-1 text-[10px] font-semibold text-text-body"
                              style={{ borderColor: 'var(--border-subtle)' }}
                            >
                              {b.batch_code}
                            </p>
                            <button
                              onClick={() => { close(); if (b.is_excluded) onRestore(b); else onExclude(b) }}
                              disabled={busy || !canWrite}
                              title={canWrite ? undefined : READ_ONLY_TITLE}
                              className="rounded px-1.5 py-1 text-left text-[10px] text-text-secondary transition-colors hover:bg-bg-input hover:text-danger disabled:opacity-40"
                            >
                              {b.is_excluded ? '↩ 계산에 복원' : '✕ 계산에서 제외'}
                            </button>
                            <button
                              onClick={() => { close(); onHistory(b) }}
                              className="rounded px-1.5 py-1 text-left text-[10px] text-text-secondary transition-colors hover:bg-bg-input hover:text-accent-teal"
                            >
                              ⋯ 액션 이력
                            </button>
                          </div>
                        )}
                      </Popover>
                    </div>
                    <span className="rounded px-1 text-[9px]" style={chipStyle(b.batch_type)}>
                      {b.batch_type}
                    </span>
                    {b.is_excluded && b.exclude_type && (
                      <span
                        className="rounded px-1 text-[9px] text-danger"
                        style={{ border: '1px solid var(--border-subtle)' }}
                        title={b.exclude_reason || ''}
                      >
                        {EXCLUDE_TYPE_LABELS[b.exclude_type]}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            {/* 서브컬럼(M84-09·62) — 중량이 왼쪽, 비율이 오른쪽. */}
            <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              {visible.map((b) => [
                <th
                  key={`${b.id}-mg`}
                  onClick={() => onSelect(b.id)}
                  className="cursor-pointer px-2 pb-1 text-center text-[9px] font-normal text-text-secondary"
                  style={colStyle(b)}
                >
                  mg/T
                </th>,
                <th
                  key={`${b.id}-pct`}
                  onClick={() => onSelect(b.id)}
                  className="cursor-pointer px-2 pb-1 text-center text-[9px] font-normal text-text-secondary"
                  style={colStyle(b)}
                >
                  %
                </th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {xKeys.map((k) => (
              <tr key={k} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <th
                  scope="row"
                  className="whitespace-nowrap px-2 py-1.5 text-left font-normal text-text-body"
                  style={STICKY_HEAD}
                >
                  {k}
                </th>
                <td className="whitespace-nowrap px-2 py-1.5 text-center text-[10px] text-text-secondary">
                  {rangeLabel(varOf(k), total)}
                </td>
                {visible.map((b) => [
                  <td
                    key={`${b.id}-mg`}
                    onClick={() => onSelect(b.id)}
                    className="cursor-pointer px-1 py-1 text-center text-text-body"
                    style={colStyle(b)}
                  >
                    {valueCell(b, k, 'mg')}
                  </td>,
                  <td
                    key={`${b.id}-pct`}
                    onClick={() => onSelect(b.id)}
                    className="cursor-pointer px-1 py-1 text-center text-text-body"
                    style={colStyle(b)}
                  >
                    {valueCell(b, k, 'pct')}
                  </td>,
                ])}
              </tr>
            ))}
            {/* 합계 행(M1002-02) — 중량 성분(balance 포함) 합. non-weight 는 mg 라 빠진다. */}
            {xKeys.length > 0 && (
              <tr className="border-t" style={{ borderColor: 'var(--border-soft)' }} data-testid="composition-total-row">
                <th
                  scope="row"
                  className="whitespace-nowrap px-2 py-1.5 text-left font-medium text-text-secondary"
                  style={STICKY_HEAD}
                  title="중량 성분(balance 포함) 합 — selected(non-weight)는 mg 로만 관리해 합계에서 뺍니다"
                >
                  Total (%)
                </th>
                <td className="whitespace-nowrap px-2 py-1.5 text-center text-[10px] text-text-muted">non-weight 제외</td>
                {visible.map((b) => {
                  const d = pctByBatch.get(b.id)
                  return (
                    <td
                      key={b.id}
                      colSpan={2}
                      onClick={() => onSelect(b.id)}
                      className={`cursor-pointer px-2 py-1.5 text-center font-semibold tabular-nums ${d?.sumOk ? 'text-text-body' : 'text-warn'}`}
                      style={colStyle(b)}
                      title={d?.sumOk ? undefined : '조성 합이 100% 가 아닙니다(±0.1%p) — 이 배치는 학습에서 빠집니다'}
                    >
                      {d?.sumShown ?? '—'}
                    </td>
                  )
                })}
              </tr>
            )}
            {xKeys.length === 0 && (
              <tr className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <td colSpan={visible.length * 2 + 2} className="px-2 py-1.5 text-center text-[10px] text-text-secondary">
                  성분(X) 값이 아직 없습니다 — Step 3에서 X 변수를 정의하십시오.
                </td>
              </tr>
            )}

            {/* 하단 y행 — 성분 행과 굵은 선으로 가른다(정본 §5.8). f2는 전용 행이다. */}
            <tr className="border-t-2" style={{ borderColor: 'var(--border-soft)' }}>
              <th
                scope="row"
                className="whitespace-nowrap px-2 py-1.5 text-left font-medium"
                style={{ ...STICKY_HEAD, color: 'var(--color-accent-teal)' }}
              >
                y · f2 (min)
              </th>
              <td />
              {visible.map((b) => (
                <td
                  key={b.id}
                  colSpan={2}
                  onClick={() => onSelect(b.id)}
                  className="cursor-pointer px-2 py-1.5 text-center font-semibold"
                  style={{ ...colStyle(b), color: b.f2 != null ? 'var(--color-accent-teal)' : undefined }}
                  title={Object.entries(b.f2_by_solution ?? {}).map(([ph, v]) => `pH ${ph}: ${v.toFixed(1)}`).join('\n')}
                >
                  {b.f2 != null ? b.f2.toFixed(1) : '—'}
                </td>
              ))}
            </tr>
            {yKeys.map((k) => (
              <tr key={k} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <th
                  scope="row"
                  className="whitespace-nowrap px-2 py-1.5 text-left font-medium"
                  style={{ ...STICKY_HEAD, color: 'var(--color-accent-purple)' }}
                >
                  y · {k}
                </th>
                <td />
                {visible.map((b) => (
                  <td
                    key={b.id}
                    colSpan={2}
                    onClick={() => onSelect(b.id)}
                    className="cursor-pointer px-2 py-1.5 text-center text-text-body"
                    style={colStyle(b)}
                  >
                    {fmtNum(b.y_values[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {visible.length === 0 && (
        <p className="mt-1 text-[10px] text-text-secondary">이 필터에 해당하는 배치가 없습니다.</p>
      )}
      {!hasTotal && xKeys.length > 0 && (
        <p className="mt-1 text-[10px] text-text-muted">
          총중량(Step 3 §3.1)이 없어 <strong>mg/T</strong> 환산을 할 수 없습니다 — 총중량을 먼저 입력하십시오.
        </p>
      )}
    </>
  )
}
